import { Button, Stack } from '@mui/material'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { useState } from 'react'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import { ResourceSteeringGridToolbar } from '@/features/flexBalance/components/ResourceSteeringGridToolbar'
import { ResourceSteeringModal } from '@/features/flexBalance/components/ResourceSteeringModal'
import type { FlexBalanceResource } from '@/features/flexBalance/types'
import { SteeringCommand } from '@/features/flexBalance/types'
import SteeringStatusChip from '@/features/resource/components/SteeringStatusChip'

type FlexBalanceGridProps = {
  flexBalanceResources: FlexBalanceResource[]
}

type ResourceSteeringRenderCell = GridRenderCellParams<FlexBalanceResource>

function ResourceSteeringGrid({ flexBalanceResources }: Readonly<FlexBalanceGridProps>) {
  const [modalOpen, setModalOpen] = useState(false)
  const [resourceName, setResourceName] = useState<string | undefined>(undefined)
  const [steeringCommand, setSteeringCommand] = useState<SteeringCommand | undefined>(undefined)

  const handleClose = () => {
    setModalOpen(false)
  }

  const openModal = (resourceId: string, resourceName: string, steeringCommand: SteeringCommand) => {
    setResourceName(resourceName)
    setSteeringCommand(steeringCommand)
    setModalOpen(true)
  }

  const columns: GridColDef<FlexBalanceResource>[] = [
    {
      field: 'name',
      headerName: 'Resource Name',
      flex: 0.5,
    },
    {
      field: 'site',
      headerName: 'Site',
      flex: 0.5,
    },
    {
      field: 'resourceId',
      headerName: 'Resource ID',
      flex: 1,
    },
    {
      field: 'steeringStatus',
      headerName: 'Steering status',
      flex: 0.5,
      renderCell: ({ row: { steeringStatus } }: ResourceSteeringRenderCell) => (
        <SteeringStatusChip status={steeringStatus} />
      ),
    },
    {
      field: 'action',
      headerName: '',
      flex: 0.5,
      renderCell: (params) => {
        return (
          <Stack
            alignItems="center"
            direction="row"
            flexDirection="row"
            height="100%"
            justifyContent="center"
            spacing={2}
          >
            <Button
              type="button"
              variant="contained"
              onClick={() => {
                openModal(params.row.resourceId, params.row.name, SteeringCommand.STEER)
              }}
            >
              Steer
            </Button>

            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                openModal(params.row.resourceId, params.row.name, SteeringCommand.RELEASE)
              }}
            >
              Release
            </Button>
          </Stack>
        )
      },
    },
  ]

  return (
    <>
      <ResourceSteeringModal
        command={steeringCommand}
        handleClose={handleClose}
        open={modalOpen}
        resourceName={resourceName}
      />
      <CustomDataGrid
        disableColumnMenu
        disableColumnSorting
        aria-label="Resource Steering"
        columns={columns}
        getRowId={(row: FlexBalanceResource) => row.resourceId}
        rows={flexBalanceResources ?? []}
        slots={{
          toolbar: ResourceSteeringGridToolbar,
        }}
        title="Resource Steering"
      />
    </>
  )
}

export default ResourceSteeringGrid
